import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushRect, pushSegment } from '../glyphs/frame';
import type { ParamValues } from '../paramSchema';
import { t } from '../../translation/markers';

const PARAMS = {
  finSpacing: { kind: 'float', label: t('fin spacing'), default: 0.06, min: 0.02, max: 0.3, decimals: 2, distance: true },
} as const;

/**
 * Radiator RUN along a wall (LINE anchor + WALL constraint): draw a span and
 * the glyph skins it — a thin body rectangle filled with evenly-spaced fin
 * lines derived from the drawn length and the fin spacing. Resizing the run
 * (end handles) or changing the spacing reflows the fins. The back rests
 * against the wall.
 */
function buildRadiatorGlyph(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;

  pushRect(out, frame, -hw, hw, -hd, hd);

  // Fins: as many spacing-sized cells as fit (at least one), stretched evenly
  // so they exactly fill the run.
  const spacing = Math.max(0.01, params.finSpacing);
  const finCells = Math.max(1, Math.round(frame.width / spacing));
  const actualSpacing = frame.width / finCells;
  for (let i = 1; i < finCells; i++) {
    const u = -hw + i * actualSpacing;
    pushSegment(out, at(frame, u, -hd), at(frame, u, hd), frame.z);
  }

  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'radiator',
    label: t('Radiator'),
    category: SketchObjectCategory.FIXTURES,
    anchor: SketchObjectAnchorKind.LINE,
    constraint: { kind: 'WALL', defaultInset: 0.03 },
    defaultSize: { width: 1.0, depth: 0.1 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildRadiatorGlyph },
  }),
);
